import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * apc-colour-overview
 *
 * Drives the colour overview section: "All colours" / "My colours" views,
 * client-side title search, printing, lazy-loading of the collection pages
 * beyond the first, and the per-customer "My colours" list.
 *
 * The list keeps the legacy `askphill_my_collection` data shape so existing
 * customers (and the customer metafield `askphill.my_collection`) carry over:
 *
 *   { lastUpdated: 1720000000000, items: { "7137376370733": { inMyCollection: true, updatedAt: 1720000000000 } } }
 *
 * Removals are stored as `inMyCollection: false` rather than deleted so that
 * two devices can be merged by per-item timestamp. Guests keep the list in
 * localStorage only; logged-in customers additionally sync it to the
 * serverless endpoint (`data-endpoint` + POST /api/myColorsCollection), which
 * writes the customer metafield.
 */

const STORAGE_KEY = 'askphill_my_collection';
const REMOTE_PATH = '/api/myColorsCollection';
const PUSH_DELAY = 800;
const SEARCH_DELAY = 150;
const PRINT_IMAGE_TIMEOUT = 4000;
const PAGE_FETCH_ATTEMPTS = 2;
// Not `view`: Shopify reserves `?view=` for alternate templates.
const VIEW_PARAM = 'tab';

/**
 * @typedef {{ inMyCollection: boolean, updatedAt: number }} ColourEntry
 * @typedef {{ lastUpdated: number | null, items: Record<string, ColourEntry> }} ColourCollection
 */

/** @returns {ColourCollection} */
const emptyCollection = () => ({ lastUpdated: null, items: {} });

/**
 * @param {unknown} value
 * @returns {value is ColourCollection}
 */
const isCollection = (value) =>
  Boolean(value) &&
  typeof value === 'object' &&
  'items' in /** @type {object} */ (value) &&
  typeof (/** @type {any} */ (value).items) === 'object' &&
  /** @type {any} */ (value).items !== null;

/** @returns {ColourCollection | null} */
function readLocal() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
    return isCollection(parsed) ? parsed : null;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/** @param {ColourCollection} collection */
function writeLocal(collection) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collection));
  } catch {
    // Storage full or disabled — the in-memory state still drives the UI.
  }
}

/**
 * Merges two collections item by item, keeping the entry with the latest
 * `updatedAt` for every product (the legacy conflict resolution).
 *
 * @param {ColourCollection} a
 * @param {ColourCollection} b
 * @returns {ColourCollection}
 */
function mergeCollections(a, b) {
  /** @type {ColourCollection} */
  const merged = {
    lastUpdated: Math.max(a.lastUpdated ?? 0, b.lastUpdated ?? 0) || null,
    items: {},
  };

  for (const productId of new Set([...Object.keys(a.items), ...Object.keys(b.items)])) {
    const entryA = a.items[productId];
    const entryB = b.items[productId];

    if (entryA && entryB) {
      merged.items[productId] = { ...(entryA.updatedAt >= entryB.updatedAt ? entryA : entryB) };
    } else {
      merged.items[productId] = { ...(entryA ?? entryB) };
    }
  }

  return merged;
}

/**
 * @typedef {Object} ApcColourOverviewRefs
 * @property {HTMLUListElement} grid
 * @property {HTMLElement} resultCount
 * @property {HTMLElement} liveRegion
 * @property {HTMLScriptElement} [remoteState]
 * @property {HTMLButtonElement} [tabAll]
 * @property {HTMLButtonElement} [tabMine]
 * @property {HTMLElement} [countAll]
 * @property {HTMLElement} [countMine]
 * @property {HTMLInputElement} [searchInput]
 * @property {HTMLElement} [loading]
 * @property {HTMLElement} [loadError]
 * @property {HTMLButtonElement} [printButton]
 * @property {HTMLElement} [noResults]
 * @property {HTMLElement} [noResultsQuery]
 * @property {HTMLElement} [emptyState]
 */

/**
 * @extends {Component<ApcColourOverviewRefs>}
 */
class ApcColourOverview extends Component {
  requiredRefs = ['grid', 'resultCount', 'liveRegion'];

  /** @type {'all' | 'mine'} */
  #view = 'all';
  #query = '';
  /** @type {ColourCollection} */
  #collection = emptyCollection();
  #abortController = new AbortController();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pushTimer;
  /** True while local changes have not been confirmed by the endpoint. */
  #pushPending = false;
  /** Bumped on every local change so a stale response cannot clear newer changes. */
  #pushVersion = 0;
  /** Resolves once every collection page has been requested (see #loadRemainingPages). */
  #pagesLoaded = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller per connection: the previous one is aborted on disconnect.
    this.#abortController = new AbortController();
    this.#collection = this.#initCollection();
    this.#view = this.#initialView();

    if (this.refs.searchInput) {
      this.#query = this.refs.searchInput.value.trim().toLowerCase();
    }

    this.#applyOwnedState(this.#drops);
    this.#render();

    window.addEventListener('pagehide', this.#flushPush, { signal: this.#abortController.signal });
    this.#pagesLoaded = this.#loadRemainingPages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#flushPush();
    this.#abortController.abort();
  }

  /* -------------------------------------------------------------------- */
  /* Declarative handlers (on:click / on:input)                            */
  /* -------------------------------------------------------------------- */

  /** @param {Event} event */
  showAll(event) {
    const link = event.target instanceof HTMLElement ? event.target.dataset.link : undefined;

    if (link) {
      window.location.assign(link);
      return;
    }

    this.#setView('all');
  }

  showMine() {
    this.#setView('mine');
  }

  /** @param {Event} event */
  handleSearch(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#debouncedSearch(event.target.value);
  }

  /** @param {Event} event */
  toggleColour(event) {
    const button = event.target instanceof HTMLElement ? event.target.closest('button') : null;
    const drop = button?.closest('[data-colour-drop]');

    if (!(button instanceof HTMLButtonElement) || !(drop instanceof HTMLElement)) return;

    const productId = drop.dataset.productId;
    if (!productId) return;

    const owned = !this.#isOwned(productId);
    const now = Date.now();

    this.#collection.items[productId] = { inMyCollection: owned, updatedAt: now };
    this.#collection.lastUpdated = now;
    writeLocal(this.#collection);
    this.#schedulePush();

    this.#setDropState(drop, owned);
    this.#announce(owned ? this.dataset.announcementAdded : this.dataset.announcementRemoved, drop.dataset.title ?? '');
    this.#render();
  }

  async print() {
    // The remaining collection pages may still be loading; wait for them so the
    // printout is complete.
    this.refs.printButton?.setAttribute('aria-busy', 'true');
    await this.#pagesLoaded;
    this.refs.printButton?.removeAttribute('aria-busy');

    // Lazy images below the fold may not be fetched yet; load the visible view's
    // images first so the printout does not contain blank drops.
    const images = /** @type {HTMLImageElement[]} */ ([
      ...this.refs.grid.querySelectorAll('[data-colour-drop]:not([hidden]) img'),
    ]);

    for (const image of images) image.loading = 'eager';

    await Promise.race([
      Promise.allSettled(images.filter((image) => !image.complete).map((image) => image.decode())),
      new Promise((resolve) => setTimeout(resolve, PRINT_IMAGE_TIMEOUT)),
    ]);

    window.print();
  }

  /* -------------------------------------------------------------------- */
  /* State                                                                 */
  /* -------------------------------------------------------------------- */

  get #isLoggedIn() {
    return Boolean(this.dataset.customerEmail);
  }

  get #showMyColours() {
    return this.dataset.showMyColours === 'true';
  }

  /** @returns {HTMLElement[]} */
  get #drops() {
    return /** @type {HTMLElement[]} */ ([...this.refs.grid.querySelectorAll('[data-colour-drop]')]);
  }

  /** @param {string} productId */
  #isOwned(productId) {
    return this.#collection.items[productId]?.inMyCollection === true;
  }

  get #ownedCount() {
    return Object.values(this.#collection.items).filter((entry) => entry.inMyCollection).length;
  }

  /**
   * Reconciles the local (localStorage) and remote (customer metafield,
   * embedded by Liquid) lists on page load — same rules as the legacy theme.
   *
   * @returns {ColourCollection}
   */
  #initCollection() {
    const local = readLocal();

    if (!this.#isLoggedIn) {
      const collection = local ?? emptyCollection();
      if (!local) writeLocal(collection);
      return collection;
    }

    const remote = this.#readRemote();

    if (!remote?.lastUpdated && !local?.lastUpdated) {
      const collection = emptyCollection();
      writeLocal(collection);
      return collection;
    }

    if (local?.lastUpdated && !remote?.lastUpdated) {
      this.#schedulePush();
      return local;
    }

    if (remote?.lastUpdated && !local?.lastUpdated) {
      writeLocal(remote);
      return remote;
    }

    const merged = mergeCollections(/** @type {ColourCollection} */ (local), /** @type {ColourCollection} */ (remote));
    writeLocal(merged);

    if (JSON.stringify(merged) !== JSON.stringify(remote)) {
      this.#schedulePush();
    }

    return merged;
  }

  /** @returns {ColourCollection | null} */
  #readRemote() {
    try {
      const parsed = JSON.parse(this.refs.remoteState?.textContent?.trim() || 'null');
      return isCollection(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /** @returns {'all' | 'mine'} */
  #initialView() {
    if (!this.#showMyColours) return 'all';
    return new URL(window.location.href).searchParams.get(VIEW_PARAM) === 'mine' ? 'mine' : 'all';
  }

  /** @param {'all' | 'mine'} view */
  #setView(view) {
    if (this.#view === view) return;
    this.#view = view;

    const url = new URL(window.location.href);
    if (view === 'mine') {
      url.searchParams.set(VIEW_PARAM, view);
    } else {
      url.searchParams.delete(VIEW_PARAM);
    }
    history.replaceState(history.state, '', url.toString());

    this.#render();
  }

  #debouncedSearch = debounce(
    /** @param {string} value */ (value) => {
      this.#query = value.trim().toLowerCase();
      this.#render();
    },
    SEARCH_DELAY,
  );

  /* -------------------------------------------------------------------- */
  /* Rendering                                                             */
  /* -------------------------------------------------------------------- */

  /** @param {HTMLElement[]} drops */
  #applyOwnedState(drops) {
    for (const drop of drops) {
      this.#setDropState(drop, this.#isOwned(drop.dataset.productId ?? ''));
    }
  }

  /**
   * @param {HTMLElement} drop
   * @param {boolean} owned
   */
  #setDropState(drop, owned) {
    drop.toggleAttribute('data-owned', owned);

    const toggle = drop.querySelector('button[aria-pressed]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    toggle.setAttribute('aria-pressed', String(owned));
    const label = owned ? toggle.dataset.labelRemove : toggle.dataset.labelAdd;
    if (label) toggle.setAttribute('aria-label', label);
  }

  #render() {
    const { refs } = this;
    const drops = this.#drops;
    const mine = this.#view === 'mine';
    let visible = 0;
    let ownedInGrid = 0;

    for (const drop of drops) {
      const owned = drop.hasAttribute('data-owned');
      if (owned) ownedInGrid += 1;

      const matchesView = !mine || owned;
      const matchesQuery = this.#query === '' || (drop.dataset.search ?? '').includes(this.#query);
      const show = matchesView && matchesQuery;

      drop.hidden = !show;
      if (show) visible += 1;
    }

    refs.resultCount.textContent = String(visible);
    if (refs.countAll) refs.countAll.textContent = String(drops.length);
    if (refs.countMine) refs.countMine.textContent = String(ownedInGrid);

    this.#setTabState(refs.tabAll, !mine);
    this.#setTabState(refs.tabMine, mine);

    const emptyMine = mine && ownedInGrid === 0 && !this.hasAttribute('data-loading');
    if (refs.emptyState) refs.emptyState.hidden = !emptyMine;
    refs.grid.hidden = emptyMine;

    const noResults = !emptyMine && visible === 0 && this.#query !== '';
    if (refs.noResults) {
      refs.noResults.hidden = !noResults;
      if (refs.noResultsQuery) refs.noResultsQuery.textContent = this.#query;
    }
  }

  /**
   * Tabs are theme buttons: the active view renders as primary, the other as secondary.
   *
   * @param {HTMLButtonElement | undefined} tab
   * @param {boolean} active
   */
  #setTabState(tab, active) {
    if (!tab) return;
    tab.setAttribute('aria-pressed', String(active));
    tab.classList.toggle('button-primary', active);
    tab.classList.toggle('button-secondary', !active);
  }

  /**
   * @param {string | undefined} template
   * @param {string} title
   */
  #announce(template, title) {
    if (!template) return;
    this.refs.liveRegion.textContent = template.replace('[title]', title);
    setTimeout(() => {
      this.refs.liveRegion.textContent = '';
    }, 5000);
  }

  /* -------------------------------------------------------------------- */
  /* Remaining collection pages (Section Rendering API)                    */
  /* -------------------------------------------------------------------- */

  async #loadRemainingPages() {
    const pages = Number(this.dataset.pages ?? 1);
    const currentPage = Number(this.dataset.currentPage ?? 1);
    const sectionId = this.dataset.sectionId;

    if (pages <= 1 || !sectionId) return;

    this.setAttribute('data-loading', '');
    const { signal } = this.#abortController;
    // Liquid rendered `currentPage` (the URL may carry ?page=N). Pages before it
    // are inserted ahead of the rendered drops, pages after it appended, so the
    // grid keeps the collection order whichever page was rendered first.
    const firstRendered = this.refs.grid.firstElementChild;
    let failed = false;

    for (let page = 1; page <= pages; page += 1) {
      if (page === currentPage) continue;
      if (signal.aborted) return;

      const drops = await this.#fetchPage(sectionId, page, signal);

      if (drops === null) {
        failed = true;
        continue;
      }

      for (const drop of drops) {
        if (page < currentPage && firstRendered) {
          this.refs.grid.insertBefore(drop, firstRendered);
        } else {
          this.refs.grid.appendChild(drop);
        }
      }

      this.#applyOwnedState(drops);
      this.#render();
    }

    if (signal.aborted) return;

    this.removeAttribute('data-loading');
    if (this.refs.loadError) this.refs.loadError.hidden = !failed;
    this.#render();
  }

  /**
   * Fetches one collection page through the Section Rendering API and returns
   * its drops. Retries once on failure; returns null when the page could not
   * be loaded so the caller can continue with the remaining pages.
   *
   * @param {string} sectionId
   * @param {number} page
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement[] | null>}
   */
  async #fetchPage(sectionId, page, signal) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));

    for (let attempt = 0; attempt < PAGE_FETCH_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Section render returned ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return /** @type {HTMLElement[]} */ ([...html.querySelectorAll('[data-colour-drop]')]);
      } catch (error) {
        if (signal.aborted) return null;
        console.error(`[apc-colour-overview] Failed to load colours page ${page} (attempt ${attempt + 1})`, error);
      }
    }

    return null;
  }

  /* -------------------------------------------------------------------- */
  /* Remote sync                                                           */
  /* -------------------------------------------------------------------- */

  get #remoteUrl() {
    const endpoint = (this.dataset.endpoint ?? '').replace(/\/+$/, '');
    return endpoint ? `${endpoint}${REMOTE_PATH}` : null;
  }

  /** Coalesces rapid toggles into one request. */
  #schedulePush() {
    if (!this.#isLoggedIn || !this.#remoteUrl) return;

    this.#pushPending = true;
    this.#pushVersion += 1;
    clearTimeout(this.#pushTimer);
    this.#pushTimer = setTimeout(() => this.#push(), PUSH_DELAY);
  }

  #payload() {
    return JSON.stringify({ email: this.dataset.customerEmail, myCollection: this.#collection });
  }

  async #push() {
    const url = this.#remoteUrl;
    if (!url) return;

    // The change stays pending until the endpoint confirms it, so a page leave
    // during (or after a failed) request still sends the beacon below.
    const version = this.#pushVersion;

    try {
      // No Content-Type header on purpose: the endpoint JSON.parses a raw
      // text/plain body (the legacy theme sent it the same way).
      const response = await fetch(url, {
        method: 'POST',
        body: this.#payload(),
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        console.error('[apc-colour-overview] Failed to sync my colours', response.status);
        return;
      }

      if (version === this.#pushVersion) this.#pushPending = false;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('[apc-colour-overview] Failed to sync my colours', error);
    }
  }

  /** Sends a pending sync immediately when the page is being left. */
  #flushPush = () => {
    if (!this.#pushPending) return;

    clearTimeout(this.#pushTimer);
    this.#pushPending = false;

    const url = this.#remoteUrl;
    if (url) navigator.sendBeacon(url, this.#payload());
  };
}

if (!customElements.get('apc-colour-overview')) {
  customElements.define('apc-colour-overview', ApcColourOverview);
}
